/**
 * Packages entity config — drives EntityTable and EntityDetailShell (11E.4).
 *
 * Subjects show NAMES not ids everywhere (list, detail) — subjectNameById is
 * threaded in via the column render closures built in Packages.tsx, since
 * the config itself doesn't have access to the subjects list.
 */

import React from 'react';
import { Badge, StatusBadge } from '@/components/common/Badge';
import type { EntityTableConfig, EntityDetailConfig } from '@/components/entity/types';
import type { PackageResponse } from '@features/catalog/api/packages';
import { formatPaise } from '@features/catalog/config/ui';
import { resolvePackageDisplayName } from './nameDerivation';

export function buildPackagesTableConfig(
  subjectNameById: Map<string, string>,
): EntityTableConfig<PackageResponse> {
  return {
    entityName: 'package',
    apiPath: '/v1/catalog/packages',
    routeBase: '/packages',
    testIdPrefix: 'candeza-package',
    defaultSort: 'name',
    defaultOrder: 'asc',
    defaultFilters: { status: 'active' },

    columns: [
      {
        key: 'name',
        header: 'Package',
        isPrimary: true,
        sortable: true,
        helpKey: 'packages.name_preview',
        render: (p) => {
          const subjectNames = p.subject_ids.map((id) => subjectNameById.get(id) ?? id);
          return <span style={{ fontWeight: 500 }}>{resolvePackageDisplayName(p, subjectNames)}</span>;
        },
      },
      {
        key: 'subjects',
        header: 'Subjects',
        hideOnMobile: true,
        render: (p) => (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 240 }}>
            {p.subject_ids.map((id) => (
              <Badge key={id} variant="primary" size="sm">{subjectNameById.get(id) ?? id}</Badge>
            ))}
          </div>
        ),
      },
      {
        key: 'type',
        header: 'Type',
        hideOnMobile: true,
        helpKey: 'packages.type',
        render: (p) => (p.package_type === 'subscription' ? (p.billing_cycle ?? 'subscription') : 'one-time'),
      },
      {
        key: 'price',
        header: 'Price',
        hideOnMobile: true,
        render: (p) => formatPaise(p.price),
      },
      {
        key: 'published_online',
        header: 'Published',
        hideOnMobile: true,
        helpKey: 'packages.published_online',
        render: (p) => (p.published_online ? <Badge variant="success" size="sm">Public</Badge> : <span style={{ color: '#94a3b8' }}>—</span>),
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        render: (p) => <StatusBadge status={p.status} />,
      },
    ],

    filters: [
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        helpKey: 'packages.filter.status',
        options: [
          { value: 'all', label: 'All Status' },
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Archived' },
        ],
      },
      {
        key: 'subject_id',
        label: 'Subject',
        type: 'select',
        helpKey: 'packages.filter.subject',
        options: [
          { value: 'all', label: 'All Subjects' },
          ...Array.from(subjectNameById.entries()).map(([id, name]) => ({ value: id, label: name })),
        ],
      },
    ],

    rowActions: ['view', 'edit', 'archive', 'activate'],
  };
}

export function buildPackageDetailConfig(props: {
  subjectNames: string[];
  renderOverviewTab: (packageId: string) => React.ReactNode;
}): EntityDetailConfig<PackageResponse> {
  return {
    entityName: 'package',
    routeBase: '/packages',
    testIdPrefix: 'candeza-package',
    getTitle: (p) => resolvePackageDisplayName(p, props.subjectNames),
    getSubtitle: (p) => (p.package_type === 'subscription' ? p.billing_cycle ?? undefined : `${p.sessions_count} sessions`),
    getStatus: (p) => p.status,
    tabs: [
      { id: 'overview', label: 'Overview', lazyLoad: false, render: props.renderOverviewTab },
    ],
  };
}

